// The top bar (slice 05; design/07-ui-shell.md §5): every feature behind a
// menu, plus the drop-downs the person made and named. A menubar in the
// ARIA sense: Left and Right move between menus, Down or Enter opens one,
// Up and Down walk its items, Escape closes and returns to the bar, so every
// menu is reachable without a pointer.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { SlotId } from '@dragons-table/vtt-shared';
import { coarsePointer } from './device';
import { SLOT_LABELS, type ToolDef } from './tools';

export type MenuItem =
  | { kind: 'tool'; tool: ToolDef; placed: SlotId | null }
  | { kind: 'action'; id: string; label: string; onSelect: () => void; checked?: boolean; disabled?: boolean; reason?: string }
  | { kind: 'separator' };

export interface Menu {
  id: string;
  label: string;
  items: MenuItem[];
  /** A drop-down the person made; empty ones say so. */
  custom?: boolean;
}

export interface TopBarProps {
  menus: Menu[];
  /** A tool chosen from a menu: the shell opens its placement chooser. */
  onTool: (tool: ToolDef, menuId: string) => void;
  /** What sits at the right of the bar: the table's name, the window it is. */
  trailing?: ReactNode;
  /** The site's origin, for the mark's link to its front page. */
  siteUrl: string;
  /** The phone layout (issue 1242): the menus and `trailing` behind one button, and `bars` beside it. */
  phone?: boolean;
  /** What opens each side bar, in the phone layout. */
  bars?: ReactNode;
}

/**
 * The line under the campaign's name (issue 1541): "Scene · <name> · <system>". The scene's name is the DM's alone
 * (a player is sent no scene list, so theirs is the system only); either part may be missing, and with neither, nothing.
 */
export function sceneLine(scene: string | null | undefined, system: string | null | undefined): string | null {
  const parts = [scene ? `Scene · ${scene}` : null, system || null].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

const selectable = (item: MenuItem) => item.kind === 'tool' || (item.kind === 'action' && !item.disabled);

export function TopBar({ menus, onTool, trailing, siteUrl, phone = false, bars }: TopBarProps) {
  // The phone layout's one button (issue 1242): the row of menus, shown over the board until a choice or a tap outside.
  const [menusOpen, setMenusOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [itemIndex, setItemIndex] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);
  const topRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const openMenu = menus.find((m) => m.id === open) ?? null;
  const openIndex = openMenu ? menus.indexOf(openMenu) : -1;

  useEffect(() => {
    if (!open && !menusOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) { setOpen(null); setMenusOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, menusOpen]);

  useEffect(() => {
    if (openMenu) itemRefs.current[itemIndex]?.focus();
  }, [openMenu, itemIndex]);

  const focusTop = (i: number) => {
    const n = ((i % menus.length) + menus.length) % menus.length;
    setActive(n);
    topRefs.current[n]?.focus();
    return n;
  };

  const show = (i: number) => {
    const menu = menus[i];
    if (!menu) return;
    setActive(i);
    setOpen(menu.id);
    const first = menu.items.findIndex(selectable);
    setItemIndex(first < 0 ? 0 : first);
  };

  const close = (refocus = true) => {
    setOpen(null);
    if (refocus) topRefs.current[active]?.focus();
  };

  const step = (from: number, dir: 1 | -1): number => {
    if (!openMenu) return from;
    const n = openMenu.items.length;
    if (n === 0) return 0;
    let i = from;
    for (let k = 0; k < n; k += 1) {
      i = (i + dir + n) % n;
      if (selectable(openMenu.items[i]!)) return i;
    }
    return from;
  };

  const onTopKey = (e: KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); const n = focusTop(i + 1); if (open) show(n); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); const n = focusTop(i - 1); if (open) show(n); }
    else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(i); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Home') { e.preventDefault(); focusTop(0); }
    else if (e.key === 'End') { e.preventDefault(); focusTop(menus.length - 1); }
  };

  const onItemKey = (e: KeyboardEvent) => {
    if (!openMenu) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setItemIndex((i) => step(i, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setItemIndex((i) => step(i, -1)); }
    else if (e.key === 'Home') { e.preventDefault(); setItemIndex(step(-1, 1)); }
    else if (e.key === 'End') { e.preventDefault(); setItemIndex(step(0, -1)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); show(focusTop(openIndex + 1)); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); show(focusTop(openIndex - 1)); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') { close(false); }
  };

  const activate = (item: MenuItem) => {
    if (item.kind === 'tool') { close(); setMenusOpen(false); onTool(item.tool, openMenu?.id ?? ''); }
    else if (item.kind === 'action' && !item.disabled) { close(); setMenusOpen(false); item.onSelect(); }
  };

  return (
    // The site's frame (issue 1541): 46 px of chrome over a gold-dim edge, dark in both themes as the site's header is
    // (`vtt-chrome` keeps the dark tokens), the "DT" mark, the menus in Cinzel.
    <div ref={barRef} className={`vtt-chrome relative flex min-h-[2.875rem] ${phone ? 'flex-nowrap gap-x-2' : 'flex-wrap gap-x-4'} items-center gap-y-1 border-b border-vtt-gold-dim bg-vtt-chrome ${phone ? 'px-2' : 'px-3.5'} text-sm shadow-[0_1px_0_#000]`} data-testid="top-bar">
      {/* The way to the site's front page (issue 1220). On a desktop the site and the table are separate tabs; on a touch device they share one. */}
      <a href={`${siteUrl}/`} {...(coarsePointer() ? {} : { target: '_blank', rel: 'noopener' })} aria-label="The Dragon's Table — front page"
        className="grid h-7 w-7 shrink-0 place-items-center rounded-[3px] border border-vtt-gold font-display text-xs font-bold text-vtt-gold hover:bg-vtt-raised" data-testid="top-bar-mark">DT</a>
      {phone && (
        <button type="button" aria-expanded={menusOpen} aria-label="Menus" data-testid="phone-menus" onClick={() => { setOpen(null); setMenusOpen((v) => !v); }}
          className={`shrink-0 rounded-[3px] border px-2.5 py-1 text-base leading-none ${menusOpen ? 'border-vtt-gold text-vtt-gold' : 'border-vtt-line text-vtt-soft'}`}>☰</button>
      )}
      {phone && bars}
      {/* Wraps rather than runs off the screen at a large text size (issue 1174). */}
      <div className={phone ? `${menusOpen ? '' : 'hidden'} vtt-chrome absolute inset-x-0 top-full z-40 border-b border-vtt-gold-dim bg-vtt-chrome p-2` : 'contents'} data-testid="top-bar-menus">
      <div role="menubar" aria-label="Table menus" className="flex flex-wrap items-center gap-1">
        {menus.map((menu, i) => (
          <div key={menu.id} className={phone ? undefined : 'relative'}>
            <button type="button" role="menuitem" aria-haspopup="menu" aria-expanded={open === menu.id}
              ref={(el) => { topRefs.current[i] = el; }} tabIndex={i === active ? 0 : -1}
              className={`rounded-[3px] px-2.5 py-1.5 font-display text-[0.78125rem] font-semibold tracking-[0.06em] hover:bg-vtt-raised ${open === menu.id ? 'bg-vtt-raised text-vtt-gold' : 'text-vtt-soft'}`}
              onClick={() => (open === menu.id ? close() : show(i))}
              onMouseEnter={() => { if (open && open !== menu.id) show(i); }}
              onKeyDown={(e) => onTopKey(e, i)}>
              {menu.label}
            </button>
            {open === menu.id && (
              <div role="menu" aria-label={menu.label} data-testid={`menu-${menu.id}`}
                className={`absolute ${phone ? 'inset-x-2 max-h-[calc(100vh-7.5rem)]' : `${i > menus.length / 2 ? 'right-0' : 'left-0'} max-h-[calc(100vh-5rem)] min-w-52 max-w-[calc(100vw-1rem)]`} top-full z-40 mt-1 overflow-y-auto vtt-popover py-1 shadow-lg`}>
                {menu.items.length === 0 && (
                  <p className="px-3 py-1 text-xs text-stone-500">Nothing here yet — add tools from any tool's chooser.</p>
                )}
                {menu.items.map((item, j) => {
                  if (item.kind === 'separator') return <hr key={`sep-${j}`} className="my-1 border-stone-700" role="separator" />;
                  const checked = item.kind === 'action' ? item.checked : undefined;
                  const disabled = item.kind === 'action' ? Boolean(item.disabled) : false;
                  return (
                    <button key={item.kind === 'tool' ? `tool-${item.tool.id}` : item.id} type="button"
                      role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={checked}
                      aria-disabled={disabled || undefined} tabIndex={j === itemIndex ? 0 : -1}
                      title={item.kind === 'action' && disabled ? item.reason : undefined}
                      ref={(el) => { itemRefs.current[j] = el; }}
                      className={`flex w-full items-center justify-between gap-4 px-3 py-1 text-left ${disabled ? 'text-stone-600' : 'hover:bg-stone-800'}`}
                      onMouseEnter={() => setItemIndex(j)} onKeyDown={onItemKey} onClick={() => activate(item)}>
                      <span>{item.kind === 'tool' ? item.tool.title : item.label}
                        {/* Why a greyed item is greyed (issue 1383): seen under it, and read as its description from the title. */}
                        {item.kind === 'action' && disabled && item.reason && <span aria-hidden="true" className="block text-xs text-stone-500">{item.reason}</span>}
                      </span>
                      {item.kind === 'tool' && item.placed && <span className="text-xs text-stone-500">{SLOT_LABELS[item.placed].toLowerCase()}</span>}
                      {checked !== undefined && <span aria-hidden="true">{checked ? '✓' : ''}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* The table's name, who you are and the build share what the menus leave, the name shortening first, so a portrait
          tablet (712 px) keeps one bar; only when under 8rem is left does it wrap to a line of its own. */}
      <div className={phone ? `mt-1 flex-wrap items-center justify-end gap-x-4 gap-y-1 overflow-hidden text-vtt-muted ${open ? 'hidden' : 'flex'}` : 'ml-auto flex min-w-[8rem] flex-1 basis-0 items-center justify-end gap-x-4 overflow-hidden whitespace-nowrap text-vtt-muted'} data-testid="top-bar-trailing">{trailing}</div>
      </div>
    </div>
  );
}
